import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, Save, Loader2, Eye, EyeOff, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Iter-27.73 — Onglet Paramètres du backoffice asso :
 *  - Changer son mot de passe
 *  - (à venir) préférences notifications, langue, etc.
 */
export default function AssoSettings({ asso }) {
    const [form, setForm] = useState({ current: "", next: "", confirm: "" });
    const [show, setShow] = useState({ current: false, next: false });
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (form.next !== form.confirm) {
            toast.error("Les deux nouveaux mots de passe ne correspondent pas");
            return;
        }
        if (form.next.length < 8) {
            toast.error("Le nouveau mot de passe doit faire au moins 8 caractères");
            return;
        }
        setSaving(true);
        try {
            await api.post("/asso/me/password", {
                current_password: form.current,
                new_password: form.next,
            });
            toast.success("Mot de passe mis à jour");
            setForm({ current: "", next: "", confirm: "" });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="asso-settings">
            {/* Section Compte */}
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-3xl p-5 sm:p-6 bg-white/3 border border-white/10"
            >
                <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="w-4 h-4 text-shariti-yellow" />
                    <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-shariti-yellow">
                        Compte
                    </p>
                </div>
                <h3 className="text-lg font-extrabold text-white mb-4">Informations</h3>
                <dl className="space-y-3 text-sm">
                    <Row label="Nom de l'association" value={asso?.name} />
                    <Row label="Type de partenariat" value={tierLabel(asso?.tier)} />
                </dl>
            </motion.div>

            {/* Section Sécurité */}
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="rounded-3xl p-5 sm:p-6 bg-white/3 border border-white/10"
                data-testid="asso-password-form"
            >
                <div className="flex items-center gap-2 mb-1">
                    <Lock className="w-4 h-4 text-shariti-orange-light" />
                    <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-shariti-orange-light">
                        Sécurité
                    </p>
                </div>
                <h3 className="text-lg font-extrabold text-white mb-4">
                    Modifier le mot de passe
                </h3>

                <div className="space-y-3">
                    <PwdInput
                        label="Mot de passe actuel"
                        value={form.current}
                        onChange={(v) => setForm({ ...form, current: v })}
                        show={show.current}
                        onToggle={() => setShow({ ...show, current: !show.current })}
                        testId="settings-pwd-current"
                    />
                    <PwdInput
                        label="Nouveau mot de passe"
                        value={form.next}
                        onChange={(v) => setForm({ ...form, next: v })}
                        show={show.next}
                        onToggle={() => setShow({ ...show, next: !show.next })}
                        testId="settings-pwd-new"
                        hint="8 caractères minimum"
                    />
                    <PwdInput
                        label="Confirmer le nouveau mot de passe"
                        value={form.confirm}
                        onChange={(v) => setForm({ ...form, confirm: v })}
                        show={show.next}
                        onToggle={() => setShow({ ...show, next: !show.next })}
                        testId="settings-pwd-confirm"
                    />

                    <button
                        onClick={submit}
                        disabled={!form.current || !form.next || !form.confirm || saving}
                        data-testid="settings-pwd-save"
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-shariti-orange hover:bg-shariti-orange/90 text-white font-extrabold px-5 py-2.5 rounded-2xl text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {saving ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Save className="w-4 h-4" />
                        )}
                        Mettre à jour le mot de passe
                    </button>
                </div>
            </motion.div>
        </div>
    );
}


function Row({ label, value, mono = false }) {
    return (
        <div className="flex items-start justify-between gap-3">
            <dt className="text-[11px] font-thin tracking-[0.15em] uppercase text-white/45">
                {label}
            </dt>
            <dd
                className={`text-white/85 text-right break-words ${
                    mono ? "font-mono text-xs" : "font-bold text-sm"
                }`}
            >
                {value || "—"}
            </dd>
        </div>
    );
}


function PwdInput({ label, value, onChange, show, onToggle, testId, hint }) {
    return (
        <div>
            <label className="text-[10px] font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                {label}
            </label>
            <div className="relative">
                <input
                    type={show ? "text" : "password"}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={testId}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 pr-11 text-white text-sm outline-none focus:border-shariti-orange/50"
                />
                <button
                    type="button"
                    onClick={onToggle}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/45 hover:text-white"
                    aria-label={show ? "Cacher" : "Afficher"}
                >
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
            </div>
            {hint && <p className="text-[10px] text-white/40 mt-1">{hint}</p>}
        </div>
    );
}


function tierLabel(tier) {
    return (
        {
            signature: "Partenaire Signature",
            premium: "Partenaire Premium",
            impact: "Partenaire Impact",
            engaged: "Partenaire Engagé",
        }[tier] || "Partenaire"
    );
}
